import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';

import Typography from '@ui/Typography';
import AvatarOptionsWrapper from '@avatar/components/AvatarOptions/AvatarOptionsWrapper';
import { Avatar, AvatarVariant } from '@avatar/types';
import { useActorsWithFiltersQuery } from '@avatar/graphql/filterAvatars/operation.generated';
import { AvatarWithVariant } from '../ActorsList';
import ActorsListWrapper from '../ActorsListWrapper';
import AvatarVariantListItem from '../AvatarVariantListItem/AvatarVariantListItem';
import {
  AvatarOrderByOrder,
  StudioProjectVersion,
} from '../../../../../../server/graphql/__generated.types';

export interface AvatarVariantsListProps {
  avatarVariants: Array<AvatarWithVariant>;
  listType: 'recents' | 'favorites';
  activeAvatarId?: number;
  activeAvatarVariantId?: number;
  handleAvatarClick: (
    avatar: Avatar,
    location: 'list' | 'modal',
    variant: AvatarVariant,
    variantVersion?: string,
    onClose?: () => void
  ) => void;
  projectVersion: StudioProjectVersion;
  disabled?: boolean | null;
}

const useStyles = makeStyles()(theme => ({
  container: {
    paddingBottom: theme.spacing(0.5),
  },
  avatarsList: {
    marginTop: theme.spacing(1.5),
  },
  avatarsListWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

export default function AvatarVariantsList(props: AvatarVariantsListProps) {
  const { classes } = useStyles();
  const {
    avatarVariants,
    activeAvatarId,
    activeAvatarVariantId,
    handleAvatarClick,
    listType,
    projectVersion,
    disabled,
  } = props;

  const { refetch: onRefetch } = useActorsWithFiltersQuery({
    variables: {
      inputs: {
        projectVersion,
        orderBy: AvatarOrderByOrder.NameAsc,
      },
    },
  });

  const title = listType === 'favorites' ? 'Favorites' : 'Recently used';
  const emptyMessage =
    listType === 'favorites'
      ? 'Favorited voices will appear here for each project'
      : 'Recently used voices will appear here for each project';

  return (
    <ActorsListWrapper onBlur={() => {}}>
      <Box className={classes.container}>
        <Box>
          <Typography color="primary" variant="pSm" component="h4">
            {title}
          </Typography>
        </Box>
        <Box component="ul" className={classes.avatarsList}>
          {avatarVariants.length ? (
            avatarVariants.map((avatarWithVariant, index) => (
              <li
                key={`${avatarWithVariant.avatar.id}-${avatarWithVariant.variant.id}`}
                className={classes.avatarsListWrapper}
              >
                <Box flexGrow={1}>
                  <AvatarVariantListItem
                    avatar={avatarWithVariant.avatar}
                    variant={avatarWithVariant.variant}
                    isActive={
                      typeof activeAvatarId !== 'undefined' &&
                      avatarWithVariant.avatar.id === activeAvatarId &&
                      avatarWithVariant.variant.id === activeAvatarVariantId
                      // @to-do: Should we check the version as well?
                    }
                    onClick={() =>
                      handleAvatarClick(
                        avatarWithVariant.avatar,
                        'list',
                        avatarWithVariant.variant,
                        avatarWithVariant.variantVersion || undefined
                      )
                    }
                    aria-label={`Select ${avatarWithVariant.avatar.name} as voice to be used within Studio.`}
                    disabled={disabled}
                  />
                </Box>
                {/** TODO: Note we are offsetting here instead of AvatarVariantListItem due to rendering issues */}
                {avatarWithVariant.avatar.accessLevel !== 'LOCKED' &&
                  projectVersion !== StudioProjectVersion.ScriptStudio && (
                    <Box marginTop={0.375}>
                      <AvatarOptionsWrapper
                        id={`${avatarWithVariant.avatar.id}`}
                        projectVersion={projectVersion}
                        avatar={avatarWithVariant.avatar}
                        variant={avatarWithVariant.variant}
                        onSelect={() => {
                          handleAvatarClick(
                            avatarWithVariant.avatar,
                            'list',
                            avatarWithVariant.variant
                          );
                        }}
                        activeAvatarId={activeAvatarId}
                        activeAvatarVariantId={activeAvatarVariantId}
                        onRefetch={() =>
                          onRefetch({
                            inputs: {
                              projectVersion,
                            },
                          })
                        }
                      />
                    </Box>
                  )}
              </li>
            ))
          ) : (
            <Box>
              <Typography color="tertiary" variant="pSm">
                {emptyMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ActorsListWrapper>
  );
}
